import React, { useState, useEffect, useMemo, useCallback, memo } from "react";
import {
  PlusOutlined,
  DeleteOutlined,
  MessageOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../assets/Icons/Editicon.svg";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchTemplates } from "../../store/templateSlice";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import { Skeleton, Space, Button, Tooltip } from "antd";
import moment from "moment";
import { deleteTemplate } from "../../assets/api/templateService";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "./Template.scss";
import DeleteTemplateModal from "../../components/TemplateComp/DeleteTemplateModal/DeleteTemplateModal";
import EmailEditTemplateModal from "../../components/TemplateComp/Email/EditTemplateModal/EmailEditTemplateModal";
import SmsEditTemplateModal from "../../components/TemplateComp/SMS/EditTemplateModal/SmsEditTemplateModal";
import EditTemplateModal from "../../components/TemplateComp/EditTemplateModal/EditTemplateModal";

const EMPTY = "—";

const StaticSkeleton = () => <Skeleton.Input size="small" active />;

const NameCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="templateEmpty">{EMPTY}</span>;
  return <span className="templateName">{text}</span>;
});
NameCell.displayName = "NameCell";

const MemoizedTextCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="templateEmpty">{EMPTY}</span>;
  return <span className="templateText">{text}</span>;
});
MemoizedTextCell.displayName = "MemoizedTextCell";

const CapitalizedCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="templateEmpty">{EMPTY}</span>;
  return (
    <span className="templateText">
      {text.charAt(0).toUpperCase() + text.slice(1)}
    </span>
  );
});
CapitalizedCell.displayName = "CapitalizedCell";

const LanguageCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="templateEmpty">{EMPTY}</span>;
  return <span className="templateLang">{text.toUpperCase()}</span>;
});
LanguageCell.displayName = "LanguageCell";

/* The .statusIndicator / .activeStatus classes this used to lean on are only
   styled inside ProductComp and UserList, so antd's stock green/red Tag showed
   through here. A dot chip, matching the status pills on the other pages. */
const StatusCell = memo(({ isActive, loading }) => {
  if (loading) return <StaticSkeleton />;
  return (
    <span className={`templateStatus ${isActive ? "is-active" : "is-inactive"}`}>
      <i />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
});
StatusCell.displayName = "StatusCell";

const CreatedAtCell = memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="templateEmpty">{EMPTY}</span>;
  const when = moment(text);
  if (!when.isValid()) return <span className="templateEmpty">{EMPTY}</span>;
  return (
    <span className="templateDate">
      <span className="templateDate__day">{when.format("DD MMM YYYY")}</span>
      <span className="templateDate__time">{when.format("hh:mm A")}</span>
    </span>
  );
});
CreatedAtCell.displayName = "CreatedAtCell";

const Template = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { channel: routeChannel } = useParams();
  const location = useLocation();

  const channel = useMemo(() => {
    if (routeChannel) return routeChannel.toLowerCase();
    const path = location.pathname.toLowerCase();
    if (path.includes("/sms")) return "sms";
    if (path.includes("/email")) return "email";
    return "whatsapp";
  }, [routeChannel, location.pathname]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [loadSubmit, setLoadSubmit] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editTemplateData, setEditTemplateData] = useState(null);

  const { templates_list, templates_list_loading } = useSelector((state) => state.template);
  const { token } = useSelector((state) => state.auth);
  const loading = templates_list_loading;

  const listData = templates_list?.data?.results || templates_list?.data?.templates;
  const finalListData = useMemo(() => {
    return Array.isArray(listData) ? listData : [];
  }, [listData]);

  // Reset page pagination on channel change
  useEffect(() => {
    setCurrentPage(1);
  }, [channel]);

  useEffect(() => {
    dispatch(fetchTemplates({ page: currentPage, page_size: pageSize, channel }));
  }, [dispatch, currentPage, pageSize, channel]);

  const handleDeleteClick = useCallback((record) => {
    setDeleteTemplateId(record.id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleEditClick = useCallback((record) => {
    setEditTemplateData(record);
    setIsEditModalOpen(true);
  }, []);

  const handleTableChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteTemplateId(null);
  }, []);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setEditTemplateData(null);
  }, []);

  const handleDeleteTemplateConfirm = useCallback(async () => {
    try {
      setLoadSubmit(true);
      const res = await deleteTemplate(deleteTemplateId, token);
      if (res.status === 200 || res.status === 204 || res.status === 201) {
        CustomNotification.show("Template deleted successfully", "success");
        setIsDeleteModalOpen(false);
        setDeleteTemplateId(null);
        dispatch(fetchTemplates({ page: currentPage, page_size: pageSize, channel }));
      }
    } catch (error) {
      CustomNotification.show(error || "Failed to delete template", "error");
    } finally {
      setLoadSubmit(false);
    }
  }, [deleteTemplateId, token, currentPage, pageSize, channel, dispatch]);

  const handleEditTemplateSave = useCallback(() => {
    dispatch(fetchTemplates({ page: currentPage, page_size: pageSize, channel }));
  }, [currentPage, pageSize, channel, dispatch]);

  const handleAddTemplateClick = useCallback(() => {
    navigate(`/communication/${channel}/create-template`);
  }, [navigate, channel]);

  const columns = useMemo(() => [
    {
      title: "Portfolio",
      dataIndex: "portfolio_name",
      key: "portfolio_name",
      render: (text) => <MemoizedTextCell text={text} loading={loading} />,
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (text) => <CapitalizedCell text={text} loading={loading} />,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <NameCell text={text} loading={loading} />,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      render: (text) => <LanguageCell text={text} loading={loading} />,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive) => <StatusCell isActive={isActive} loading={loading} />,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => <CreatedAtCell text={text} loading={loading} />,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 96,
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;
        return (
          <Space size={8}>
            <Tooltip title="Edit template">
              <Button
                className="templateIconBtn is-edit"
                icon={<EditIcon />}
                onClick={() => handleEditClick(record)}
              />
            </Tooltip>
            <Tooltip title="Delete template">
              <Button
                className="templateIconBtn is-delete"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteClick(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ], [loading, handleEditClick, handleDeleteClick]);

  const skeletonData = useMemo(() =>
    Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );

  const totalItems = useMemo(() =>
    templates_list?.data?.count !== undefined
      ? templates_list?.data?.count
      : (templates_list?.data?.total_count || finalListData.length),
    [templates_list, finalListData]
  );

  const headerTitle = useMemo(() => {
    switch (channel) {
      case "email":
        return "All Email Templates";
      case "sms":
        return "All SMS Templates";
      case "whatsapp":
      default:
        return "All WhatsApp Templates";
    }
  }, [channel]);

  /* The panel icon names the channel the list is showing, the way every other
     PanelCard on the app carries a glyph for its section. */
  const headerIcon = useMemo(() => {
    switch (channel) {
      case "email":
        return <MailOutlined />;
      case "sms":
        return <MessageOutlined />;
      case "whatsapp":
      default:
        return <WhatsAppOutlined />;
    }
  }, [channel]);

  const renderEditModal = () => {
    if (!isEditModalOpen) return null;
    switch (channel) {
      case "email":
        return (
          <EmailEditTemplateModal
            isOpen={isEditModalOpen}
            onClose={handleEditModalClose}
            templateData={editTemplateData}
            onSave={handleEditTemplateSave}
          />
        );
      case "sms":
        return (
          <SmsEditTemplateModal
            isOpen={isEditModalOpen}
            onClose={handleEditModalClose}
            templateData={editTemplateData}
            onSave={handleEditTemplateSave}
          />
        );
      case "whatsapp":
      default:
        return (
          <EditTemplateModal
            isOpen={isEditModalOpen}
            onClose={handleEditModalClose}
            templateData={editTemplateData}
            onSave={handleEditTemplateSave}
          />
        );
    }
  };

  return (
    <div className="templatePageMainWrapper">
      <DeleteTemplateModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        loading={loadSubmit}
        onDelete={handleDeleteTemplateConfirm}
      />

      {renderEditModal()}

      {/* Flush body + an inner `*-report-table` wrapper is how the dashboard
          panels carry a table, so the rows sit at the same inset here. */}
      <PanelCard
        title={headerTitle}
        icon={headerIcon}
        accent="brand"
        padded={false}
        actions={
          <Button
            className="addTemplateBtn"
            icon={<PlusOutlined />}
            onClick={handleAddTemplateClick}
          >
            Add Template
          </Button>
        }
      >
        <div className="template-report-table">
          <ReusableTable
            columns={columns}
            dataSource={loading ? skeletonData : finalListData}
            totalItems={totalItems}
            rowKey="id"
            CurrentPageNo={currentPage}
            pageSize={pageSize}
            triggerChange={handleTableChange}
          />
        </div>
      </PanelCard>
    </div>
  );
};

export default memo(Template);
